import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import Footer from "../../../../common/Footer";
import Header from "../../../../common/Header";
import { useNavigate, useParams } from "react-router-dom";
import LeftSideNavigation from "../../../../common/LeftSideNavigation";
import { config } from "../../../../config/config";
import { apiList, invokeApi } from "../../../../services/apiServices";
import { useCookies } from "react-cookie";

// ==================== Styled Components ====================
const ScrollImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

// ==================== Main Component ====================
const ProgrammeDetailPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const { ogUrl } = useParams(); // Get the programme OG URL from route params
  const [cookies] = useCookies(); // Use react-cookie hook

  const [programmeData, setProgrammeData] = useState([]);
  const [currentProgramme, setCurrentProgramme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ============ Fetch Dynamic Programme Data ============
  useEffect(() => {
    const fetchProgramme = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await invokeApi(
          config.getMyCollege + apiList.getProgrammes,
          null,
          cookies
        );

        console.log("ProgrammeDetailPage: API Response:", response);

        if (response?.status === 200) {
          const responseData = response.data;
          let programmes = [];

          if (Array.isArray(responseData)) {
            programmes = responseData;
          } else if (responseData?.programmes && Array.isArray(responseData.programmes)) {
            programmes = responseData.programmes;
          } else if (responseData?.data && Array.isArray(responseData.data)) {
            programmes = responseData.data;
          } else if (responseData?.items && Array.isArray(responseData.items)) {
            programmes = responseData.items;
          }

          console.log("ProgrammeDetailPage: Found programmes:", programmes.length);

          if (programmes.length > 0) {
            setProgrammeData(programmes);

            if (ogUrl) {
              const cleanOgUrl = ogUrl.replace(/^\/+|\/+$/g, ""); // Remove leading/trailing slashes
              console.log("ProgrammeDetailPage: Searching for ogUrl:", cleanOgUrl);

              const matchedProgramme = programmes.find(
                (p) => (p.ogUrl || "").replace(/^\/+|\/+$/g, "") === cleanOgUrl
              );

              if (matchedProgramme) {
                console.log("ProgrammeDetailPage: Found match:", matchedProgramme.programmeTitle);
                setCurrentProgramme(matchedProgramme);
              } else {
                console.warn("ProgrammeDetailPage: No match found for ogUrl, defaulting to first item.");
                setCurrentProgramme(programmes[0]);
              }
            } else {
              setCurrentProgramme(programmes[0]);
            }
          } else {
            setError("No programmes found");
          }
        } else {
          setError(`Failed to fetch programmes: ${response?.status}`);
        }
      } catch (error) {
        console.error("ProgrammeDetailPage: API error:", error);
        setError("Failed to load programme data");
        setProgrammeData([]);
        setCurrentProgramme(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProgramme();
    window.scroll(0, 0);
  }, [ogUrl, cookies]);

  if (loading) {
    return (
      <>
        <Header />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
            flexDirection: "column",
            p: 3,
          }}
        >
          <Typography color="error" variant="h6" gutterBottom>
            {error}
          </Typography>
          <Typography variant="body1">
            Please check your connection and try again.
          </Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <Header />

      {/* ===== Hero Section ===== */}
      {/* <Box
        sx={{
          width: "100%",
          height: isMobile ? "50vh" : "65vh",
          position: "relative",
        }}
      >
        {currentProgramme ? (
          <Box sx={{ width: "100%", height: "100%" }}>
            <ScrollImage
              src={
                currentProgramme.featuredUrl ||
                currentProgramme.bannerUrl ||
                "/default-banner.jpg"
              }
              alt={currentProgramme.programmeTitle || "Programme Banner"}
              onError={(e) => {
                e.target.src = "/default-banner.jpg";
              }}
            />
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              backgroundColor: "#f5f5f5",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" color="textSecondary">
              No programme data available
            </Typography>
          </Box>
        )}
      </Box> */}

      {/* ===== Main Content Section ===== */}
      <Grid
        container
        sx={{
          px: isMobile ? 2 : 4,
          py: isMobile ? 2 : 4,
          maxWidth: "100%",
          margin: "0 auto",
          mt: isMobile ? 10 : 8
        }}
      >
        {/* Left Side Navigation */}
        {!isMobile && (
          <Grid
            item
            md={2}
            sx={{
              position: "sticky",
              top: 80,
              height: "calc(100vh - 64px)",
              alignSelf: "flex-start",
              overflowY: "auto",
              zIndex: 1,
              pr: 2,
            }}
          >
            <LeftSideNavigation />
          </Grid>
        )}

        {/* Right Side Content */}
        <Grid
          item
          xs={12}
          md={10}
          sx={{
            border: "1px solid #7f6b56",
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {currentProgramme ? (
            <Box
              sx={{
                color: "#7f6b56",
                fontFamily: "Inter, sans-serif",
                px: isMobile ? 2 : 3,
                py: isMobile ? 3 : 6,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  mt: 2,
                  lineHeight: 1.6,
                  fontFamily: "Montserrat, sans-serif",
                  fontSize: isMobile ? "16px" : "20px",
                  fontWeight: "500",
                  textAlign: "justify",
                  alignItems: "center",
                  "& p, & div, & span, & li": {
                    textAlign: "justify",
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: currentProgramme.programmeContent || "No content available",
                }}
              />
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <Typography variant="h6" color="textSecondary">
                Select a programme to view details
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Mobile Navigation */}
        {isMobile && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <LeftSideNavigation />
          </Grid>
        )}
      </Grid>

      <Footer variant="light" />
    </>
  );
};

export default ProgrammeDetailPage;
